import type {
  ConsumptionMethodRecord, HabitRecord, PurchaseTemplateRecord,
} from '../core/types.js';
import { el, clear, toast } from '../core/dom.js';
import { logPurchase, logSkippedPurchase, startCoolingOff } from '../progress/purchase-log.js';
import { events as eventsStore } from '../storage/db.js';
import { showUndoToast } from '../components/undo-toast.js';
import { markEventUndone } from '../storage/db.js';
import { trapFocus } from '../components/focus-trap.js';

interface LastPurchase {
  templateId: string;
  amount: number;
  currency: string;
  label: string;
  methodId?: string | undefined;
  occurredAt: string;
}

async function findLastPurchase(
  habitId: string,
  templates: PurchaseTemplateRecord[],
): Promise<LastPurchase | null> {
  const allEvents = await eventsStore.byHabit(habitId);
  const logged = allEvents
    .filter((e) => e.type === 'purchase_logged')
    .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
  for (const ev of logged) {
    const p = ev.payload as {
      purchaseId?: string; templateId?: string; methodId?: string;
      label?: string; currency?: string; amount?: number;
    };
    if (!p.templateId) continue;
    if (!templates.find((t) => t.id === p.templateId)) continue;
    if (typeof p.amount !== 'number' || typeof p.currency !== 'string' || typeof p.label !== 'string') continue;
    return {
      templateId: p.templateId,
      amount: p.amount,
      currency: p.currency,
      label: p.label,
      methodId: p.methodId,
      occurredAt: ev.occurredAt,
    };
  }
  return null;
}

function fmtMoney(amount: number, currency: string): string {
  return `${currency} ${amount.toFixed(2)}`;
}

export async function renderPurchaseLogSheet(
  habit: HabitRecord,
  methods: ConsumptionMethodRecord[],
  templates: PurchaseTemplateRecord[],
): Promise<void> {
  if (templates.length === 0) {
    toast('No purchase templates set up yet.');
    return;
  }
  const backdrop = el('div', { class: 'sheet-backdrop', attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Log a purchase' } });
  const sheet = el('div', { class: 'sheet' });
  backdrop.appendChild(sheet);
  document.body.appendChild(backdrop);
  document.body.classList.add('sheet-open');

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') dismiss();
  };
  document.addEventListener('keydown', onKey);
  backdrop.addEventListener('click', (ev) => {
    if (ev.target === backdrop) dismiss();
  });

  const last = await findLastPurchase(habit.id, templates);
  let mode: 'bought' | 'skipped' | 'cooling_off' = 'bought';
  await renderRoot();
  const releaseFocus = trapFocus(sheet);

  function dismiss(): void {
    document.removeEventListener('keydown', onKey);
    releaseFocus();
    backdrop.remove();
    document.body.classList.remove('sheet-open');
  }

  async function renderRoot(): Promise<void> {
    clear(sheet);
    sheet.appendChild(el('div', { class: 'sheet-handle' }));
    sheet.appendChild(el('h2', {},
      mode === 'bought' ? 'Log a purchase'
      : mode === 'skipped' ? 'You protected money'
      : 'Cool off first',
    ));
    sheet.appendChild(el('p', { class: 'muted' },
      mode === 'bought'
        ? 'Pick what you bought. No need to be exact.'
        : mode === 'skipped'
          ? 'Mark a purchase you almost made but skipped. Counts as money protected.'
          : 'Park an impulse buy for 24 hours. Wean will check in tomorrow.'));

    // Mode toggle
    const toggle = el('div', { class: 'kbd-list', attrs: { role: 'tablist', style: 'margin-bottom:0.75rem;' } });
    const modes: Array<{ value: typeof mode; label: string }> = [
      { value: 'bought', label: 'I bought it' },
      { value: 'skipped', label: 'I skipped it' },
      { value: 'cooling_off', label: 'Cool off 24h' },
    ];
    for (const m of modes) {
      const isActive = mode === m.value;
      const btn = el('button', {
        class: `tag${isActive ? ' active' : ''}`,
        attrs: { type: 'button', 'aria-selected': isActive ? 'true' : 'false' },
        onclick: () => { mode = m.value; void renderRoot(); },
      }, m.label);
      toggle.appendChild(btn);
    }
    sheet.appendChild(toggle);

    if (mode === 'bought' && last) {
      const tpl = templates.find((t) => t.id === last.templateId);
      if (tpl) {
        sheet.appendChild(el('button', {
          class: 'btn primary block',
          attrs: { style: 'margin-bottom: var(--space-3);' },
          onclick: async () => {
            await commit(tpl, last.amount, last.methodId);
          },
        }, `Same as last · ${tpl.label} · ${fmtMoney(last.amount, tpl.currency)}`));
      }
    }

    // Subscriptions belong in Plan > Money (cancel / renewal tracking). The
    // ad-hoc purchase log only handles one-off transactions.
    const oneOffTemplates = templates.filter((t) => t.kind !== 'subscription');
    sheet.appendChild(el('div', { class: 'stack', attrs: { style: 'gap:6px;' } },
      ...oneOffTemplates.map((t) =>
        el('button', {
          class: 'btn block',
          onclick: () => renderTemplateForm(t),
        }, t.label + (t.defaultPrice ? ` · ${fmtMoney(t.defaultPrice, t.currency)}` : '')),
      ),
    ));

    sheet.appendChild(el('button', {
      class: 'sheet-cancel',
      attrs: { type: 'button' },
      onclick: () => dismiss(),
    }, 'Cancel'));
  }

  function renderTemplateForm(template: PurchaseTemplateRecord): void {
    clear(sheet);
    sheet.appendChild(el('div', { class: 'sheet-handle' }));
    sheet.appendChild(el('h2', {}, template.label));
    let amount = template.defaultPrice ?? 0;
    const amountInput = el('input', {
      type: 'number', step: '0.01', min: '0',
      value: amount > 0 ? String(amount) : '',
      placeholder: '0.00',
      oninput: (e: Event) => {
        const v = Number((e.target as HTMLInputElement).value);
        amount = isNaN(v) ? 0 : v;
      },
    }) as HTMLInputElement;
    sheet.appendChild(el('div', { class: 'field' },
      el('label', {}, `Price (${template.currency})`),
      amountInput,
    ));
    setTimeout(() => amountInput.focus(), 50);

    let chosenMethodId = template.methodId;
    if (!chosenMethodId && methods.length > 1) {
      sheet.appendChild(el('div', { class: 'field' },
        el('label', {}, 'Method'),
        el('div', { class: 'choice-list' },
          ...methods.map((m) =>
            el('label', { class: 'choice' },
              el('input', {
                type: 'radio', name: 'pmethod',
                onchange: () => { chosenMethodId = m.id; },
              }),
              el('div', {}, m.label),
            ),
          ),
        ),
      ));
    }

    sheet.appendChild(el('div', { class: 'wizard-actions' },
      el('button', { class: 'btn ghost', onclick: () => { void renderRoot(); } }, 'Back'),
      el('button', {
        class: 'btn primary',
        onclick: async () => {
          if (Number.isNaN(amount) || amount < 0) { toast('Enter a price.'); return; }
          // Zero is fine for one-off freebies, but "I skipped" or "Cool off"
          // need a real number — saying "I protected $0" is meaningless and
          // also breaks the savings model.
          if (amount === 0 && (mode === 'skipped' || mode === 'cooling_off')) {
            toast('Enter what you would have spent.');
            return;
          }
          await commit(template, amount, chosenMethodId);
        },
      }, 'Save purchase'),
    ));
  }

  async function commit(
    template: PurchaseTemplateRecord,
    amount: number,
    methodId: string | undefined,
  ): Promise<void> {
    if (mode === 'skipped') {
      const skipEv = await logSkippedPurchase({
        habitId: habit.id,
        templateId: template.id,
        label: template.label,
        currency: template.currency,
        amountAvoided: amount,
      });
      dismiss();
      showUndoToast({
        message: `You protected ${fmtMoney(amount, template.currency)}.`,
        onUndo: async () => {
          await markEventUndone(skipEv.id, 'user_undo');
          toast('Skipped purchase undone.');
          window.dispatchEvent(new CustomEvent('wean:state-changed'));
        },
      });
      window.dispatchEvent(new CustomEvent('wean:state-changed'));
      return;
    }
    if (mode === 'cooling_off') {
      const ev = await startCoolingOff({
        habitId: habit.id,
        templateId: template.id,
        label: template.label,
        currency: template.currency,
        amount,
      });
      dismiss();
      showUndoToast({
        message: `Cooling off ${fmtMoney(amount, template.currency)}. We'll check in tomorrow.`,
        onUndo: async () => {
          await markEventUndone(ev.id, 'user_undo');
          toast('Cool-off canceled.');
          window.dispatchEvent(new CustomEvent('wean:state-changed'));
        },
      });
      window.dispatchEvent(new CustomEvent('wean:state-changed'));
      return;
    }
    const { event } = await logPurchase({
      habitId: habit.id,
      templateId: template.id,
      methodId,
      label: template.label,
      currency: template.currency,
      amount,
      packageQuantity: template.packageQuantity,
      packageUnitLabel: template.packageUnitLabel,
      source: 'quick_log',
    });
    dismiss();
    showUndoToast({
      message: `Logged ${template.label}.`,
      onUndo: async () => {
        await markEventUndone(event.id, 'user_undo');
        toast('Purchase log undone.');
        window.dispatchEvent(new CustomEvent('wean:state-changed'));
      },
    });
    window.dispatchEvent(new CustomEvent('wean:state-changed'));
  }
}
